// src/components/NodesView.tsx
import React, {useState, useMemo} from 'react';
import {Users, Package, Coins, Factory, Scale, Building2, Link, TriangleAlert} from 'lucide-react';
import {useInView} from '../../hooks/useInView';
import {useSimulationStore, NodeData, ConnectionData} from '../../store/useSimulationStore';
import SearchSortControls, {SortOption} from '../common/SearchSortControls';
import StatusBadge, {BadgePreset} from "../common/StatusBadge";
import KpiCard from "../common/KpiCard";
import StatRow from "../common/StatRow";
import ItemList, { ItemRowProps } from '../common/ItemList';
import ProgressBar from '../common/ProgressBar';
import Dot from '../common/StatusDot';
import { cardInner, cardSubtle } from '../../utils/styles';

type SortKey = 'name' | 'population' | 'resources' | 'stability' | 'emissions';
type FilterStatus = 'all' | 'poor' | 'middle' | 'rich';

// ─── NodeCard ─────────────────────────────────────────────────────────────────
// Extracted so useInView can be called at component level (rules of hooks).
interface NodeCardProps {
   node: NodeData;
   nodes: NodeData[];
   connections: ConnectionData[];
   setSelectedNodeId: (id: number | null) => void;
   setSelectedConnectionId: (id: number | null) => void;
}

const NodeCard: React.FC<NodeCardProps> = ({
                                              node, nodes, connections, setSelectedNodeId, setSelectedConnectionId,
                                           }) => {
   const [ref, inView] = useInView<HTMLDivElement>({rootMargin: '300px', once: false});
   
   const resPerCap = (node.resources || 0) / Math.max(node.population || 1, 1);
   const stabilityPct = (node.stability || 0) * 100;
   const capUsage = (node.population || 0) / Math.max(node.capacity_limit || 1, 1);
   
   const nodeConnections = connections.filter(c => c.node_a_id === node.id || c.node_b_id === node.id);
   const tradingConnections = nodeConnections.filter(c => (Number(c.last_trade_volume) || 0) > 0);
   
   // Top 3 connections: trading first, then by traffic volume
   const rankedConnections = [...nodeConnections]
      .sort((a, b) => {
         const aT = (Number(a.last_trade_volume) || 0) > 0 ? 1 : 0;
         const bT = (Number(b.last_trade_volume) || 0) > 0 ? 1 : 0;
         if (bT !== aT) return bT - aT;
         return (Number(b.traffic_volume) || 0) - (Number(a.traffic_volume) || 0);
      })
      .slice(0, 3);
   const hiddenConnections = nodeConnections.length - rankedConnections.length;
   
   const connItems: ItemRowProps[] = rankedConnections.map(conn => {
      const otherId = conn.node_a_id === node.id ? conn.node_b_id : conn.node_a_id;
      const other = nodes.find(n => n.id === otherId);
      const isTrading = (Number(conn.last_trade_volume) || 0) > 0;
      return {
         id: conn.id,
         icon: isTrading ? <Dot color="#c084fc" /> : <Dot color="#60a5fa" />,
         label: other?.name ?? `#${otherId}`,
         badge: other?.income_status,
         value: isTrading
            ? `${(Number(conn.last_trade_volume) || 0).toFixed(1)} res`
            : `${Number(conn.traffic_volume).toLocaleString()} trf`,
         valueColor: isTrading ? 'text-purple-300' : 'text-gray-500',
         onClick: (e) => setSelectedConnectionId(conn.id),
      };
   });
   
   const statusColor =
      node.income_status === 'rich' ? 'bg-yellow-900/50 border-yellow-700 text-yellow-300' :
         node.income_status === 'middle' ? 'bg-blue-900/50 border-blue-700 text-blue-300' :
            'bg-gray-700 border-gray-600 text-gray-300';
   
   return (
      <div
         ref={ref}
         onClick={() => setSelectedNodeId(node.id)}
         className="bg-gray-900 border border-gray-800 rounded-xl p-4 cursor-pointer hover:brightness-125 transition-all flex flex-col min-h-[320px]"
      >
         {inView ? (
            <>
               {/* ── Header ── */}
               <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                     <div className="text-white font-semibold text-sm truncate">{node.name}</div>
                     <div className="text-gray-600 text-xs">#{node.id}</div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-2 shrink-0">
                     {tradingConnections.length > 0 && (
                        <StatusBadge preset={'trading' as BadgePreset}>{node.income_status}</StatusBadge>
                     )}
                     <StatusBadge preset={node.income_status as BadgePreset}>{node.income_status}</StatusBadge>
                  </div>
               </div>
               
               {/* ── Key metrics ── */}
               <div className="grid grid-cols-2 gap-2 mb-3">
                  <KpiCard icon={<Users className="w-4 h-4" />} label="Population"
                           value={node.population || 0}
                           color="text-white"
                           compact={false} />
                  <KpiCard icon={<Package className="w-4 h-4" />} label="Resources"
                           value={node.resources || 0}
                           color="text-emerald-400"
                           compact={false} />
                  <KpiCard icon={<Coins className="w-4 h-4" />} label="Income"
                           value={Number(node.income || 0)}
                           color="text-yellow-400"
                           compact={false} />
                  <KpiCard icon={<Factory className="w-4 h-4" />} label="Emissions"
                           value={node.emissions || 0}
                           color="text-red-400"
                           compact={false} />
               </div>
               
               
               {/* ── Stability bar ── */}
               <div className={`rounded-lg px-2.5 py-2 mb-3 border ${stabilityPct > 35 ? cardInner : cardSubtle}`}>
                  <StatRow
                     icon={<Scale className="w-3.5 h-3.5" />}
                     label="Stability"
                     value={`${stabilityPct.toFixed(1)}%`}
                     color={
                        stabilityPct > 60 ? 'text-green-400' :
                           stabilityPct > 35 ? 'text-yellow-400' : 'text-red-400'
                     }
                  />
                  
                  <ProgressBar
                     value={stabilityPct}
                     color={stabilityPct > 60 ? 'bg-green-500' : stabilityPct > 35 ? 'bg-yellow-500' : 'bg-red-500'}
                     className="my-1.5"
                  />
                  
                  <StatRow
                     value={`${resPerCap.toFixed(1)} res/capita`}
                     align="right"
                     color="text-gray-600" />
               </div>
               
               {/* ── Housing & Capacity ── */}
               <div className={`rounded-lg px-2.5 py-2 mb-3 border ${capUsage > 0.95 ? cardInner : cardSubtle}`}>
                  <StatRow
                     icon={<Building2 className="w-3.5 h-3.5" />}
                     label="Capacity"
                     value={`${(capUsage * 100).toFixed(1)}%`}
                     color={
                        capUsage > 0.95 ? 'text-red-400' :
                           capUsage > 0.80 ? 'text-yellow-400' : 'text-blue-400'
                     }
                  />
                  <ProgressBar
                     value={capUsage * 100}
                     color={capUsage > 0.95 ? 'bg-red-500' : capUsage > 0.80 ? 'bg-yellow-500' : 'bg-blue-500'}
                     className="my-1.5"
                  />
                  <StatRow
                     label={`${(node.housing_units || 0).toLocaleString()} units`}
                     value={`cap ${(node.capacity_limit || 0).toLocaleString()}`}
                     labelColor="text-gray-600"
                     color="text-gray-600" />
                  {capUsage > 0.85 && (
                     <div className="mt-1.5 text-xs text-yellow-600 flex items-center gap-1">
                        <TriangleAlert className="w-3 h-3 shrink-0" /> Approaching capacity — auto-investing in housing if resources allow.
                     </div>
                  )}
               </div>

               {/* ── Top 3 Connections ── */}
               <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                     <StatRow icon={<Link className="w-3.5 h-3.5" />} label="Connections" value={`${nodeConnections.length} total`} labelColor="text-gray-400" color="text-gray-600" />
                  </div>
                  {rankedConnections.length === 0 ? (
                     <p className="text-gray-600 text-xs px-1">No connections.</p>
                  ) : (
                     <div className="space-y-1">
                        <ItemList
                           items={connItems}
                           maxVisible={3}
                           onOverflowClick={() => setSelectedNodeId(node.id)}
                        />
                        {hiddenConnections > 0 && (
                           <button
                              onClick={e => {
                                 e.stopPropagation();
                                 setSelectedNodeId(node.id);
                              }}
                              className="text-xs text-center text-gray-600 hover:text-gray-300 px-2.5 py-1 w-full text-left transition-colors"
                           >
                              +{hiddenConnections} more — click to inspect
                           </button>
                        )}
                     </div>
                  )}
               </div>
               
               {/* Footer */}
               <div className="mt-auto mb-0 pt-3 border-t border-white/5 text-[10px] text-gray-600 font-mono">
                  {node.x}, {node.y}, {node.z}
               </div>
            </>
         ) : (
            // Skeleton — preserves card height so grid layout doesn't reflow
            <div className="animate-pulse space-y-3 h-full flex flex-col">
               <div className="flex justify-between">
                  <div className="h-9 bg-gray-800 rounded w-2/3" />
                  <div className="h-9 bg-gray-800 rounded w-16" />
               </div>
               <div className="grid grid-cols-2 gap-2">
                  {[...Array(4)].map((_, i) => (
                     <KpiCard containerClasses="!bg-gray-800 !border-gray-800 *:!text-transparent"
                              icon={<Users className="w-4 h-4" />} label="label"
                              value={0}
                              compact={false} />
                  ))}
               </div>
               <div className="bg-gray-800 rounded-lg flex flex-col px-2.5 py-2 mb-3 justify-between text-xs *:!text-transparent">
                  <div className="mb-1.5">...</div>
                  <div className="w-full h-1.5 mb-1.5"></div>
                  <div>...</div>
                  {capUsage > 0.85 && (
                     <div className="mt-1.5 text-xs flex items-center gap-1">
                        <TriangleAlert className="w-3 h-3 shrink-0" /> Approaching capacity — auto-investing in housing if resources allow.
                     </div>
                  )}
               </div>
               <div className="bg-gray-800 rounded-lg !mb-3">
                  <div className="bg-gray-800 rounded-lg">
                     <div className="w-full h-4 mb-1.5" />
                  </div>
                  <div className="space-y-1">
                     {rankedConnections.map((conn) => (
                        <div key={conn.id} className="w-full h-[2.25rem]" />
                     ))}
                     {(rankedConnections.length === 0 || hiddenConnections > 0) && (
                        <div className="w-full h-6" />
                     )}
                  </div>
               </div>
               <div className="h-[3.9375rem] bg-gray-800 rounded-lg !mt-auto !mb-1" />
            </div>
         )}
      </div>
   );
};

// ─── NodesView ────────────────────────────────────────────────────────────────
const NodesView: React.FC = () => {
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   const setSelectedConnectionId = useSimulationStore(s => s.setSelectedConnectionId);
   
   const [search, setSearch] = useState('');
   const [sortKey, setSortKey] = useState<SortKey>('stability');
   const [sortAsc, setSortAsc] = useState(false);
   const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
   
   const filtered = useMemo(() => {
      let result = nodes.filter(n => {
         const matchesSearch = n.name.toLowerCase().includes(search.toLowerCase());
         const matchesStatus = filterStatus === 'all' || n.income_status === filterStatus;
         return matchesSearch && matchesStatus;
      });
      return [...result].sort((a, b) => {
         let av: number | string, bv: number | string;
         switch (sortKey) {
            case 'stability':
               av = a.stability || 0;
               bv = b.stability || 0;
               break;
            case 'population':
               av = a.population || 0;
               bv = b.population || 0;
               break;
            case 'resources':
               av = a.resources || 0;
               bv = b.resources || 0;
               break;
            case 'emissions':
               av = a.emissions || 0;
               bv = b.emissions || 0;
               break;
            case 'name':
               av = a.name;
               bv = b.name;
               break;
            default:
               return 0;
         }
         if (av < bv) return sortAsc ? -1 : 1;
         if (av > bv) return sortAsc ? 1 : -1;
         return 0;
      });
   }, [nodes, search, sortKey, sortAsc, filterStatus]);
   
   const toggleSort = (key: SortKey) => {
      if (sortKey === key) setSortAsc(p => !p);
      else {
         setSortKey(key);
         setSortAsc(false);
      }
   };
   
   const sortOptions: SortOption<SortKey>[] = [
      {key: 'stability', label: 'Stability'},
      {key: 'population', label: 'Pop'},
      {key: 'resources', label: 'Res'},
      {key: 'emissions', label: 'Emissions'},
      {key: 'name', label: 'Name'},
   ];
   
   return (
      <div className="space-y-3">
         
         {/* ── Controls ── */}
         <SearchSortControls
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by node name…"
            sortKey={sortKey}
            sortAsc={sortAsc}
            onSortChange={toggleSort}
            sortOptions={sortOptions}
            totalCount={nodes.length}
            filteredCount={filtered.length}
         >
            <div className="flex items-center gap-1 flex-wrap">
               {(['all', 'poor', 'middle', 'rich'] as FilterStatus[]).map(s => (
                  <StatusBadge
                     key={s}
                     onClick={() => setFilterStatus(s)}
                     active={filterStatus === s}
                     preset={s === 'all' ? 'generic' : s}
                     className={s === 'all' && filterStatus === 'all'
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : undefined}
                  >
                     {s}
                  </StatusBadge>
               ))}
            </div>
         </SearchSortControls>
         
         {/* ── Grid ── */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(node => (
               <NodeCard
                  key={node.id}
                  node={node}
                  nodes={nodes}
                  connections={connections}
                  setSelectedNodeId={setSelectedNodeId}
                  setSelectedConnectionId={setSelectedConnectionId}
               />
            ))}
            {filtered.length === 0 && (
               <div className="col-span-full text-center text-gray-600 py-16 text-sm">
                  No nodes match your search.
               </div>
            )}
         </div>
      
      </div>
   );
};

export default NodesView;
